import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { apiFetch, extractApiMessage, type ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import type {
  Camada,
  LoteNave,
  Page,
  ParteDelDiaPorCamada,
  ParteDelDiaUpsertInput,
} from '@/lib/types';
import { offlineDb, migrateParte, type PendingParte } from './db';
import { prepareOfflineBundle } from './bundle';
import {
  resolveOperationConflict,
  discardPendingOperation,
  startOperationsSync,
  syncPendingOperations,
} from './operations';
import {
  cacheReadThrough,
  clearStorageError,
  connectOfflineChannel,
  currentOwner,
  getSnapshot,
  getStorageError,
  isTemporaryFailure,
  notifyOfflineChange,
  reportStorageError,
  reportStorageFailure,
  saveSnapshot,
} from './cache';
export type { PendingParte } from './db';

const parteKey = (camadaId: number, fecha: string) =>
  ['camadas', camadaId, 'registros-diarios', fecha] as const;
const byCreated = (a: PendingParte, b: PendingParte) =>
  a.createdAt - b.createdAt || a.id.localeCompare(b.id);
const dayOperations = async (dayKey: string) =>
  (await offlineDb.operations.where('dayKey').equals(dayKey).toArray()).sort(byCreated);

export function projectParte(
  baseline: ParteDelDiaPorCamada,
  input: ParteDelDiaUpsertInput,
): ParteDelDiaPorCamada {
  return {
    ...baseline,
    local_pending: true,
    observaciones: input.observaciones ?? null,
    registros: baseline.registros.map((r) => {
      const part = input.partes.find((p) => p.lote_nave_id === r.lote_nave_id);
      return part
        ? {
            ...r,
            id: r.id ?? -r.lote_nave_id,
            bajas_muerte: part.bajas_muerte ?? 0,
            bajas_triaje: part.bajas_triaje ?? 0,
          }
        : r;
    }),
  };
}
export async function readProjectedParte(
  camadaId: number,
  fecha: string,
  baseline: ParteDelDiaPorCamada,
): Promise<ParteDelDiaPorCamada> {
  const ownerId = currentOwner();
  if (ownerId == null) return baseline;
  try {
    if (offlineDb.mutations) {
      const rows = (await offlineDb.mutations.where('ownerId').equals(ownerId).toArray())
        .filter(
          (row) =>
            row.path === `/v1/camadas/${camadaId}/registros-diarios` &&
            (row.body as { fecha?: string })?.fecha === fecha,
        )
        .sort((a, b) => a.timestamp - b.timestamp);
      return rows.reduce(
        (parte, operation) => projectParte(parte, operation.body as ParteDelDiaUpsertInput),
        baseline,
      );
    }
    const pending = await dayOperations(`${ownerId}:${camadaId}:${fecha}`);
    return pending.reduce((parte, operation) => projectParte(parte, operation.body), baseline);
  } catch (error) {
    reportStorageError(error);
    return baseline;
  }
}

function createOperation(
  camadaId: number,
  body: ParteDelDiaUpsertInput,
  baseline: ParteDelDiaPorCamada,
  ownerId: number,
  previous?: PendingParte,
): PendingParte {
  const id = crypto.randomUUID();
  return {
    id,
    ownerId,
    explotacionId: baseline.explotacion_id,
    camadaId,
    fecha: body.fecha,
    dayKey: `${ownerId}:${camadaId}:${body.fecha}`,
    body,
    payload: {
      ...body,
      operation_uuid: id,
      expected_revision: baseline.revision,
      expected_explotacion_id: baseline.explotacion_id,
    },
    baseline,
    parentId: previous?.id ?? null,
    createdAt: Math.max(Date.now(), (previous?.createdAt ?? 0) + 1),
    status: 'pending',
    attempts: 0,
  };
}
export async function enqueueParte(
  camadaId: number,
  input: ParteDelDiaUpsertInput,
  baseline: ParteDelDiaPorCamada,
): Promise<ParteDelDiaPorCamada> {
  const ownerId = currentOwner();
  if (ownerId == null) throw new Error('Inicia sesión para guardar el parte.');
  if (!baseline.revision || !baseline.explotacion_id || baseline.fecha !== input.fecha)
    throw new Error('Carga el parte con conexión antes de guardarlo sin conexión.');
  const body = structuredClone(input);
  try {
    if (offlineDb.mutations) {
      await offlineDb.transaction(
        'rw',
        offlineDb.mutations,
        offlineDb.snapshots,
        offlineDb.bundles,
        async () => {
          const rows = (await offlineDb.mutations.where('ownerId').equals(ownerId).toArray())
            .filter((row) => row.scope === `camada:${camadaId}`)
            .sort((a, b) => a.timestamp - b.timestamp);
          if (rows.some((row) => ['conflict', 'error'].includes(row.status)))
            throw new Error('Resuelve el parte pendiente antes de guardar otra edición.');
          const sameDay = rows
            .filter((row) => (row.body as { fecha?: string })?.fecha === body.fecha)
            .at(-1);
          const previous = sameDay
            ? ({ id: sameDay.id, createdAt: sameDay.timestamp } as PendingParte)
            : undefined;
          const operation = migrateParte(
            createOperation(camadaId, body, baseline, ownerId, previous),
          );
          const latest = rows.at(-1);
          if (latest && !operation.dependencies.includes(latest.id))
            operation.dependencies.push(latest.id);
          await offlineDb.mutations.put(operation);
          if (!(await getSnapshot(parteKey(camadaId, body.fecha), ownerId)))
            await saveSnapshot(parteKey(camadaId, body.fecha), baseline, ownerId);
        },
      );
      clearStorageError();
      notifyOfflineChange();
      window.dispatchEvent(new Event('offline:queued'));
      return projectParte(baseline, body);
    }
    await offlineDb.transaction('rw', offlineDb.operations, offlineDb.snapshots, async () => {
      const pending = await dayOperations(`${ownerId}:${camadaId}:${body.fecha}`);
      if (pending.some((o) => o.explotacionId !== baseline.explotacion_id))
        throw new Error('La explotación del parte ha cambiado.');
      if (pending.some((o) => o.status === 'conflict' || o.status === 'error'))
        throw new Error('Resuelve el parte pendiente antes de guardar otra edición.');
      await offlineDb.operations.put(
        createOperation(camadaId, body, baseline, ownerId, pending.at(-1)),
      );
      if (!(await getSnapshot(parteKey(camadaId, body.fecha), ownerId)))
        await saveSnapshot(parteKey(camadaId, body.fecha), baseline, ownerId);
    });
  } catch (error) {
    reportStorageFailure(error);
    throw error;
  }
  clearStorageError();
  notifyOfflineChange();
  return projectParte(baseline, body);
}

let syncing: Promise<void> | null = null;
let busy = false;
const holder = crypto.randomUUID();
const LEASE_MS = 120_000;
async function runSync() {
  const ownerId = currentOwner();
  if (ownerId == null || navigator.onLine === false) return;
  if (
    !(await offlineDb.operations.where('ownerId').equals(ownerId).toArray()).some(
      (o) => !o.parentId && o.status !== 'conflict' && o.status !== 'error',
    )
  )
    return;
  const leaseId = `sync:${ownerId}`;
  const claimed = await offlineDb.transaction('rw', offlineDb.leases, async () => {
    const lease = await offlineDb.leases.get(leaseId);
    if (lease && lease.expiresAt > Date.now() && lease.holder !== holder) return false;
    await offlineDb.leases.put({ id: leaseId, holder, expiresAt: Date.now() + LEASE_MS });
    return true;
  });
  if (!claimed) return;
  busy = true;
  notifyOfflineChange();
  const heartbeat = window.setInterval(() => {
    void offlineDb
      .transaction('rw', offlineDb.leases, async () => {
        if ((await offlineDb.leases.get(leaseId))?.holder === holder)
          await offlineDb.leases.put({ id: leaseId, holder, expiresAt: Date.now() + LEASE_MS });
      })
      .catch(reportStorageError);
  }, 30_000);
  const attempted = new Set<string>();
  try {
    while (currentOwner() === ownerId && Boolean(navigator.onLine)) {
      const rows = (await offlineDb.operations.where('ownerId').equals(ownerId).toArray()).sort(
        byCreated,
      );
      const operation = rows.find(
        (o) =>
          !o.parentId && !attempted.has(o.id) && o.status !== 'conflict' && o.status !== 'error',
      );
      if (!operation) break;
      attempted.add(operation.id);
      const sending = await offlineDb.transaction(
        'rw',
        offlineDb.operations,
        offlineDb.leases,
        async () => {
          const lease = await offlineDb.leases.get(leaseId);
          const fresh = await offlineDb.operations.get(operation.id);
          if (
            lease?.holder !== holder ||
            lease.expiresAt <= Date.now() ||
            !fresh ||
            fresh.parentId ||
            fresh.status === 'conflict' ||
            fresh.status === 'error'
          )
            return null;
          const claimed = {
            ...fresh,
            status: 'sending' as const,
            attempts: fresh.attempts + 1,
            message: undefined,
          };
          await offlineDb.operations.put(claimed);
          return claimed;
        },
      );
      if (!sending) break;
      notifyOfflineChange();
      let confirmedByServer = false;
      try {
        // The durable payload is immutable after its first attempt, including
        // an uncertain request where the server may already have committed.
        const actual = await apiFetch<ParteDelDiaPorCamada>(
          `/v1/camadas/${operation.camadaId}/registros-diarios`,
          { method: 'POST', body: JSON.stringify(operation.payload), expectedOwnerId: ownerId },
        );
        if (
          !actual.revision ||
          actual.explotacion_id !== operation.explotacionId ||
          actual.fecha !== operation.fecha
        )
          throw new Error('El servidor devolvió un parte incompatible.');
        confirmedByServer = true;
        const applied = await offlineDb.transaction(
          'rw',
          offlineDb.operations,
          offlineDb.snapshots,
          offlineDb.leases,
          async () => {
            if (
              (await offlineDb.leases.get(leaseId))?.holder !== holder ||
              !(await offlineDb.operations.get(operation.id))
            )
              return false;
            const children = (await dayOperations(operation.dayKey)).filter(
              (o) => o.parentId === operation.id,
            );
            await saveSnapshot(parteKey(operation.camadaId, operation.fecha), actual, ownerId);
            await offlineDb.operations.delete(operation.id);
            for (const child of children)
              await offlineDb.operations.put({
                ...child,
                parentId: null,
                baseline: actual,
                payload: { ...child.payload, expected_revision: actual.revision },
              });
            return true;
          },
        );
        if (applied) clearStorageError();
      } catch (error) {
        if (confirmedByServer) reportStorageError(error);
        const apiError = error as ApiError;
        const detail = (
          apiError.body as { detail?: { code?: string; actual?: ParteDelDiaPorCamada } } | null
        )?.detail;
        const status =
          apiError.status === 409 && detail?.code === 'parte_revision_obsoleta' && detail.actual
            ? 'conflict'
            : apiError.status === 401
              ? 'auth'
              : confirmedByServer || isTemporaryFailure(error)
                ? 'retry'
                : 'error';
        await offlineDb.transaction('rw', offlineDb.operations, offlineDb.leases, async () => {
          if (
            (await offlineDb.leases.get(leaseId))?.holder !== holder ||
            !(await offlineDb.operations.get(operation.id))
          )
            return;
          await offlineDb.operations.put({
            ...sending,
            status,
            message: extractApiMessage(error, 'No se pudo sincronizar.'),
            actual: status === 'conflict' ? detail?.actual : undefined,
          });
        });
        if (status === 'retry' || status === 'auth') break;
      } finally {
        notifyOfflineChange();
      }
    }
  } finally {
    window.clearInterval(heartbeat);
    await offlineDb.transaction('rw', offlineDb.leases, async () => {
      if ((await offlineDb.leases.get(leaseId))?.holder === holder)
        await offlineDb.leases.delete(leaseId);
    });
    busy = false;
    notifyOfflineChange();
  }
}
export function syncPendingParts(): Promise<void> {
  if (offlineDb.mutations) return syncPendingOperations();
  if (syncing) return syncing;
  syncing = runSync()
    .catch((error) => {
      reportStorageError(error);
      throw error;
    })
    .finally(() => {
      syncing = null;
      // runSync normally announces its own completion. If releasing the
      // lease failed, still clear the visible sending indicator once.
      if (busy) {
        busy = false;
        notifyOfflineChange();
      }
    });
  return syncing;
}

export async function resolveConflict(id: string, choice: 'server' | 'local'): Promise<void> {
  if (offlineDb.mutations) return resolveOperationConflict(id, choice);
  const ownerId = currentOwner();
  await offlineDb.transaction('rw', offlineDb.operations, offlineDb.snapshots, async () => {
    const operation = await offlineDb.operations.get(id);
    if (
      !operation ||
      operation.ownerId !== ownerId ||
      operation.status !== 'conflict' ||
      !operation.actual
    )
      throw new Error('El conflicto ya no está disponible.');
    const actual = operation.actual;
    if (actual.explotacion_id !== operation.explotacionId)
      throw new Error('La explotación ha cambiado. Recarga el parte con conexión.');
    const chain = await dayOperations(operation.dayKey);
    const latest = chain.at(-1)!;
    const partes = new Map(
      chain.flatMap((queued) =>
        queued.body.partes.map((part) => [part.lote_nave_id, part] as const),
      ),
    );
    for (const queued of chain) await offlineDb.operations.delete(queued.id);
    await saveSnapshot(parteKey(operation.camadaId, operation.fecha), actual, operation.ownerId);
    if (choice === 'local')
      await offlineDb.operations.put(
        createOperation(
          operation.camadaId,
          { ...latest.body, partes: [...partes.values()] },
          actual,
          operation.ownerId,
        ),
      );
  });
  notifyOfflineChange();
}

export async function discardPendingParte(id: string): Promise<void> {
  if (offlineDb.mutations) return discardPendingOperation(id);
  const ownerId = currentOwner();
  await offlineDb.transaction('rw', offlineDb.operations, offlineDb.leases, async () => {
    const operation = await offlineDb.operations.get(id);
    if (!operation || operation.ownerId !== ownerId)
      throw new Error('El parte pendiente no pertenece a esta sesión.');
    const chain = await dayOperations(operation.dayKey);
    if (chain.some((o) => o.status === 'sending'))
      throw new Error('Espera a que termine la sincronización antes de descartar.');
    for (const queued of chain) await offlineDb.operations.delete(queued.id);
  });
  notifyOfflineChange();
}

export async function getParteSaveError(camadaId: number, fecha: string): Promise<string | null> {
  const ownerId = currentOwner();
  if (ownerId == null) return 'La sesión ha cambiado. El parte sigue pendiente para su usuario.';
  if (offlineDb.mutations) {
    const failed = (await offlineDb.mutations.where('ownerId').equals(ownerId).toArray()).find(
      (row) =>
        row.status === 'error' &&
        row.path === `/v1/camadas/${camadaId}/registros-diarios` &&
        (row.body as { fecha?: string })?.fecha === fecha,
    );
    return failed?.message ?? null;
  }
  const failed = (await dayOperations(`${ownerId}:${camadaId}:${fecha}`)).find(
    (o) => o.status === 'error',
  );
  return failed?.message ?? null;
}

export function usePendingParts() {
  const ownerId = useAuthStore((s) => s.user?.id ?? null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const change = () => setVersion((v) => v + 1);
    window.addEventListener('offline:change', change);
    return () => window.removeEventListener('offline:change', change);
  }, []);
  const result = useLiveQuery(async () => {
    try {
      const queued =
        ownerId == null
          ? []
          : offlineDb.mutations
            ? (await offlineDb.mutations.where('ownerId').equals(ownerId).toArray())
                .filter((row) => /^\/v1\/camadas\/-?\d+\/registros-diarios$/.test(row.path))
                .map((row) => {
                  const body = row.body as ParteDelDiaUpsertInput;
                  const camadaId = Number(row.path.split('/')[3]);
                  return {
                    id: row.id,
                    ownerId: row.ownerId,
                    explotacionId: row.explotacionId,
                    camadaId,
                    fecha: body.fecha,
                    dayKey: `${row.ownerId}:${camadaId}:${body.fecha}`,
                    body,
                    payload: {
                      ...body,
                      operation_uuid: row.id,
                      expected_revision: row.expectedRevision ?? '',
                      expected_explotacion_id: row.explotacionId,
                    },
                    baseline: {} as ParteDelDiaPorCamada,
                    parentId: row.parentId,
                    createdAt: row.timestamp,
                    status: row.status,
                    attempts: row.attempts,
                    message: row.message,
                    actual: row.legacyActual,
                  } satisfies PendingParte;
                })
                .sort(byCreated)
            : (await offlineDb.operations.where('ownerId').equals(ownerId).toArray()).sort(
                byCreated,
              );
      return {
        operations: queued,
        available: true,
        error: null as string | null,
      };
    } catch (error) {
      return {
        operations: [] as PendingParte[],
        available: false,
        error: `Almacenamiento local no disponible: ${extractApiMessage(error)}`,
      };
    }
  }, [ownerId, version]);
  return {
    operations: (result?.operations ?? []).filter((o) => o.ownerId === ownerId),
    available: result?.available ?? typeof indexedDB !== 'undefined',
    error: result?.error ?? getStorageError(),
    busy,
  };
}

export function startPartsSync(onChange?: () => void) {
  if (offlineDb.mutations) return startOperationsSync(onChange);
  connectOfflineChannel();
  const sync = () => {
    if (navigator.onLine !== false) void syncPendingParts().catch(() => {});
  };
  let preparing = false;
  const foreground = () => {
    sync();
    if (currentOwner() != null && navigator.onLine !== false && !preparing) {
      preparing = true;
      void prepareOfflineParts()
        .catch(() => {})
        .finally(() => {
          preparing = false;
        });
    }
  };
  const visibility = () => {
    if (document.visibilityState === 'visible') foreground();
  };
  window.addEventListener('online', foreground);
  window.addEventListener('focus', foreground);
  document.addEventListener('visibilitychange', visibility);
  const storage = (event: StorageEvent) => {
    if (event.key?.startsWith('camadero.')) useAuthStore.getState().loadFromStorage();
  };
  window.addEventListener('storage', storage);
  if (onChange) window.addEventListener('offline:change', onChange);
  const unsubscribe = useAuthStore.subscribe((state, previous) => {
    if (state.user?.id !== previous.user?.id || state.bump !== previous.bump) foreground();
  });
  const interval = window.setInterval(() => {
    if (document.visibilityState === 'visible') sync();
  }, 30_000);
  foreground();
  return () => {
    window.clearInterval(interval);
    window.removeEventListener('storage', storage);
    window.removeEventListener('online', foreground);
    window.removeEventListener('focus', foreground);
    document.removeEventListener('visibilitychange', visibility);
    if (onChange) window.removeEventListener('offline:change', onChange);
    unsubscribe();
  };
}

export async function prepareOfflineParts() {
  if (offlineDb.bundles) return prepareOfflineBundle();
  const today = new Date();
  const fecha = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const active = await cacheReadThrough(['camadas', 'activas'], () =>
    apiFetch<Page<Camada>>('/v1/camadas?cerrada=false&limit=50'),
  );
  for (const camada of active.items) {
    await cacheReadThrough(['camadas', camada.id], () =>
      apiFetch<Camada>(`/v1/camadas/${camada.id}`),
    );
    await cacheReadThrough(['lotes-nave', 'camada', camada.id], () =>
      apiFetch<LoteNave[]>(`/v1/camadas/${camada.id}/lotes-nave`),
    );
    await cacheReadThrough(parteKey(camada.id, fecha), () =>
      apiFetch<ParteDelDiaPorCamada>(`/v1/camadas/${camada.id}/registros-diarios?fecha=${fecha}`),
    );
  }
  if (getStorageError()) throw new Error(getStorageError()!);
}
